import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Link2, FlaskConical, Flame, Snowflake, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtOdds, pct, SPORT_META } from "@/lib/odds";
import { FormDots } from "@/components/StandingsTable";
import { ShareButtons, teamShareText } from "@/components/ShareButtons";

function Stat({ l, v, c = "text-white" }) {
  return <div className="rounded-lg bg-[#121824] px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className={`font-mono-num text-xl font-bold ${c}`}>{v}</p></div>;
}

const pc = (p) => (p == null ? "text-slate-500" : p >= 55 ? "text-emerald-400" : p <= 45 ? "text-red-400" : "text-amber-400");

export function TeamShareCard({ d, innerRef }) {
  const { row: r, trends: t, model_backing: mb } = d;
  const meta = SPORT_META[d.sport];
  return (
    <div ref={innerRef} data-testid="team-share-card" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#26334D] bg-[#0B0E14] p-6" style={{ backgroundImage: `radial-gradient(ellipse 80% 40% at 50% -10%, ${meta?.color || "#10B981"}40, transparent)` }}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-emerald-400"><FlaskConical size={16} /><span className="font-display text-lg font-black uppercase tracking-tight text-white">The Parlay <span className="text-emerald-400">Lab</span></span></span>
        <span className="rounded-full border border-[#26334D] px-3 py-0.5 font-mono-num text-[10px] font-bold uppercase tracking-widest text-slate-300">{d.league}</span>
      </div>
      <p className="mt-5 text-xs uppercase tracking-widest text-slate-500">#{r.rank} of {d.teams_total} · {r.w}–{r.l}{d.draws ? `–${r.d}` : ""} · {d.points_system ? `${r.pts} pts` : r.pct.toFixed(3).replace(/^0/, "")}</p>
      <h1 className="font-display mt-1 text-4xl font-black uppercase leading-none tracking-tight text-white" data-testid="team-share-card-name">{d.team}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-xs text-slate-400">Last 5 <FormDots form={r.form} /></span>
        <span className={`font-mono-num text-xs font-bold ${r.streak.startsWith("W") ? "text-emerald-400" : r.streak.startsWith("L") ? "text-red-400" : "text-slate-400"}`}>{r.streak}</span>
        {r.heat && <span data-testid="team-share-card-heat" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.heat === "hot" ? "border-orange-500/50 bg-orange-500/10 text-orange-300" : "border-sky-500/50 bg-sky-500/10 text-sky-300"}`}>{r.heat === "hot" ? <Flame size={11} /> : <Snowflake size={11} />} {r.heat}</span>}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2" data-testid="team-share-card-trends">
        {t?.over_under && <Stat l="Overs" v={t.over_under.over_pct != null ? `${t.over_under.over_pct}%` : "—"} c={pc(t.over_under.over_pct)} />}
        {t?.ats && <Stat l="Covers" v={t.ats.cover_pct != null ? `${t.ats.cover_pct}%` : "—"} c={pc(t.ats.cover_pct)} />}
        <Stat l={t?.favorite?.w + t?.favorite?.l >= t?.underdog?.w + t?.underdog?.l ? "As favorite" : "As underdog"} v={(() => { const x = t?.favorite?.w + t?.favorite?.l >= t?.underdog?.w + t?.underdog?.l ? t?.favorite : t?.underdog; return x ? `${x.w}–${x.l}` : "—"; })()} />
        {(!t?.over_under || !t?.ats) && <Stat l="Home" v={r.home} />}
        {!t?.over_under && !t?.ats && <Stat l="Away" v={r.away} />}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2" data-testid="team-share-card-model">
        <span><p className="text-[10px] uppercase tracking-wider text-slate-500">Model backing {d.team.split(" ").slice(-1)[0]} this season</p><p className="text-sm font-semibold text-white">{mb.n ? `${mb.won}–${mb.lost} · ${mb.hit_rate}% hit rate` : "No calls graded yet"}</p></span>
        {mb.n > 0 && <span className={`font-mono-num text-2xl font-black ${pc(mb.hit_rate)}`}>{mb.hit_rate}%</span>}
      </div>
      {d.model_call && d.game && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2">
          <span className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-wider text-slate-500">Model's call today · {d.game.at_home ? "vs" : "@"} {d.game.opponent}</p><p className="truncate text-sm font-semibold text-white">{d.model_call.selection}</p></span>
          <span className="font-mono-num text-sm font-bold text-amber-400">{fmtOdds(d.model_call.odds)}</span>
          <span className="font-mono-num text-xs text-emerald-400">{pct(d.model_call.win_probability)}</span>
        </div>
      )}
      <p className="mt-4 text-[10px] uppercase tracking-widest text-slate-600">theparlaylab · AI probability picks · results simulated</p>
    </div>
  );
}

export function useTeamCardActions(ref, sport, slug) {
  const download = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `parlay-lab-${slug}.png`;
      a.click();
      toast.success("Team card downloaded");
    } catch (e) {
      toast.error("Could not render image");
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/t/${sport}/${slug}`);
    toast.success("Public link copied");
  };
  return { download, copy };
}

export function TeamShareModal({ sport, slug }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const ref = useRef(null);
  const { download, copy } = useTeamCardActions(ref, sport, slug);
  const openModal = async () => {
    setOpen(true);
    if (!d) {
      const { api } = await import("@/lib/api");
      api.get(`/teams/${sport}/${slug}/card`).then((r) => setD(r.data)).catch(() => toast.error("Could not load card"));
    }
  };
  return (
    <>
      <button data-testid="team-share-card-btn" onClick={openModal} className="btn-ghost !px-4 !py-2 text-xs"><Share2 size={14} /> Share card</button>
      {open && (
        <div data-testid="team-share-card-modal" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 py-8 backdrop-blur-sm sm:items-center" onClick={() => setOpen(false)}>
          <div className="fade-up flex w-full max-w-md flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            {d ? <TeamShareCard d={d} innerRef={ref} /> : <div className="glass-card h-72 w-full max-w-md animate-pulse" />}
            <div className="flex flex-wrap justify-center gap-3">
              <button data-testid="team-share-card-download-btn" onClick={download} disabled={!d} className="btn-primary"><Download size={16} /> Download PNG</button>
              <button data-testid="team-share-card-copy-link-btn" onClick={copy} className="btn-ghost"><Link2 size={16} /> Copy public link</button>
              <button data-testid="team-share-card-close-btn" onClick={() => setOpen(false)} className="btn-ghost !px-3"><X size={16} /></button>
            </div>
            {d && <ShareButtons text={teamShareText(d)} url={`${window.location.origin}/t/${sport}/${slug}`} testPrefix="team-share" />}
          </div>
        </div>
      )}
    </>
  );
}
